import React, { useState, useEffect } from 'react';
import { format, isValid, differenceInDays } from 'date-fns';
import { shiftCompetitionDates } from '../utils/dateUtils';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { competitionService } from '../services/competition';
import { competitionAccessService } from '../services/competitionAccess';
import { getStatusLabel, getStatusColor } from '../constants/competitionConstants';
import { setCompetitions, deleteCompetition, setCurrentCompetition, duplicateCompetition, deleteCompetitionFromDB } from '../store/competitionSlice';
import { saveCompetitionVersion } from '../store/competitionThunks';
import { useCompetitionImport } from '../hooks/useCompetitionImport';
import { alpha } from '@mui/material/styles';
import SessionList from '../components/SessionList';
import {
  Box,
  Container,
  InputAdornment,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Collapse,
  useTheme,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AddIcon from '@mui/icons-material/Add';
import ImportModal from '../components/ImportModal';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const Dashboard = () => {
  const highlightText = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    
    const parts = String(text).split(new RegExp(`(${searchTerm})`, 'gi'));
    const highlightColor = theme.palette.primary.main;
    
    return parts.map((part, index) => 
      part.toLowerCase() === searchTerm.toLowerCase() ? (
        <Box
          key={index}
          component="span"
          sx={{
            backgroundColor: alpha(highlightColor, 0.2),
            borderRadius: 0.5,
            px: 0.5,
          }}
        >
          {part}
        </Box>
      ) : part
    );
  };
  const theme = useTheme();
  const dispatch = useDispatch();
  const { user, isSuperAdmin, loading: authLoading, role } = useAuth();
  const competitions = useSelector(state => {
    // console.log('Current state:', state); // For debugging
    return state?.competition?.competitions || [];
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [sortConfig, setSortConfig] = useState({ field: 'date', direction: 'desc' });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [isLoading, setIsLoading] = useState(true);
  const [backendError, setBackendError] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingCompetition, setEditingCompetition] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', startDate: '', endDate: '' });
  const [editErrors, setEditErrors] = useState({ name: '', startDate: '', endDate: '' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [competitionToDelete, setCompetitionToDelete] = useState(null);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');

  const hasLoadedRef = React.useRef(false);

  useEffect(() => {
    const loadCompetitions = async () => {
      try {
        setIsLoading(true);
        const allCompetitions = await competitionService.getAllCompetitions();

        // Super admins see all competitions; regular users see only those they have access to
        let accessibleCompetitions = allCompetitions;
        // Only filter if auth is loaded and user is definitely not a superadmin
        if (user && role !== null && !isSuperAdmin) {
          const userAccess = await competitionAccessService.getAllUserAccess();
          const accessibleIds = new Set(Object.keys(userAccess));
          accessibleCompetitions = allCompetitions.filter(comp =>
            accessibleIds.has(comp.competition?.id)
          );
        }

        dispatch(setCompetitions(accessibleCompetitions));
        setBackendError(false);
        hasLoadedRef.current = true;
      } catch (error) {
        console.error('Error loading competitions:', error);
        setBackendError(true);
        setSnackbar({
          open: true,
          message: 'Unable to connect to server. Some features may be limited.',
          severity: 'warning'
        });
        // Still dispatch empty competitions to clear any stale data
        dispatch(setCompetitions([]));
      } finally {
        setIsLoading(false);
      }
    };

    // Only load once auth is complete and we haven't loaded yet
    if (!authLoading && user && role !== null && !hasLoadedRef.current) {
      loadCompetitions();
    } else if (!authLoading && user && role !== null) {
      // Already loaded, just clear the loading state
      setIsLoading(false);
    }
  }, [dispatch, user, isSuperAdmin, authLoading, role]);

  const isNewCompetition = (competition) => {
    if (!competition.addedAt) return false;
    const addedDate = new Date(competition.addedAt);
    const now = new Date();
    // Consider competitions added in the last 24 hours as "new"
    return (now.getTime() - addedDate.getTime()) < 24 * 60 * 60 * 1000;
  };

  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Log all competitions before filtering
  // console.log('All competitions before filtering:', competitions);

  const filteredAndSortedCompetitions = [...competitions]
    .filter(competition => {
      // Log each competition being filtered
      // console.log('Filtering competition:', competition);
      
      if (!competition?.competition) {
        // console.log('Invalid competition structure:', competition);
        return false;
      }

      const searchTerms = searchQuery.toLowerCase();
      const name = competition.competition.name?.toLowerCase() || '';
      const details = competition.competition.details?.toLowerCase() || '';
      const sanctionId = competition.competition.sanctionId?.toLowerCase() || '';
      
      const matches = name.includes(searchTerms) || 
                     details.includes(searchTerms) || 
                     sanctionId.includes(searchTerms);
      
      // Log if this competition matches the search
      if (!matches) {
        // console.log('Competition filtered out:', competition.competition.name);
      }
      
      return matches;
    })

  // Log filtered results
  // console.log('Filtered competitions:', filteredAndSortedCompetitions);
  
  const sortedCompetitions = filteredAndSortedCompetitions.sort((a, b) => {
    const direction = sortConfig.direction === 'asc' ? 1 : -1;
    
    // Safely get values with fallbacks
    const nameA = a?.competition?.name || '';
    const nameB = b?.competition?.name || '';
    const dateA = a?.competition?.startDate ? new Date(a.competition.startDate.split(' ')[0]) : new Date(0);
    const dateB = b?.competition?.startDate ? new Date(b.competition.startDate.split(' ')[0]) : new Date(0);

    if (sortConfig.field === 'name') {
      return direction * (nameA.localeCompare(nameB));
    } else if (sortConfig.field === 'date') {
      return direction * (dateA - dateB);
    }
    return 0;
  });

  // console.log(sortedCompetitions)


  const navigate = useNavigate();
  
  const handleExpandClick = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const handleDelete = (event, competition) => {
    event.stopPropagation(); // Prevent triggering the competition click
    setCompetitionToDelete(competition);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (competitionToDelete) {
      try {
        await dispatch(deleteCompetitionFromDB(competitionToDelete.competition.id)).unwrap();
        setSnackbar({
          open: true,
          message: 'Competition deleted successfully',
          severity: 'success'
        });
      } catch (error) {
        console.error('Error deleting competition:', error);
        setSnackbar({
          open: true,
          message: `Error deleting competition: ${error.message}`,
          severity: 'error'
        });
      }
    }
    setDeleteDialogOpen(false);
    setCompetitionToDelete(null);
    setDeleteConfirmationText('');
  };

  const handleEdit = (event, competition) => {
    event.stopPropagation();
    setEditingCompetition(competition);
    // Use split('T')[0] to safely extract YYYY-MM-DD from either ISO string or date string without timezone conversion
    const safeStartDate = competition.competition.startDate ? String(competition.competition.startDate).split('T')[0] : '';
    const safeEndDate = competition.competition.endDate ? String(competition.competition.endDate).split('T')[0] : '';
    
    setEditForm({
      name: competition.competition.name || '',
      startDate: safeStartDate,
      endDate: safeEndDate
    });
    setEditErrors({ name: '', startDate: '', endDate: '' });
    setEditModalOpen(true);
  };

  const handleNameChange = (value) => {
    const newForm = { ...editForm, name: value };
    const newErrors = { ...editErrors };

    newErrors.name = value.trim() ? '' : 'Competition name is required';

    setEditForm(newForm);
    setEditErrors(newErrors);
  };

  const isValidDate = (dateStr) => {
    return dateStr && dateStr.length === 10 && !isNaN(new Date(dateStr).getTime());
  };

  const handleEditSave = async () => {
    // Validate all fields
    const errors = { name: '', startDate: '', endDate: '' };
    if (!editForm.name.trim()) {
      errors.name = 'Competition name is required';
    }
    if (!editForm.startDate || !isValidDate(editForm.startDate)) {
      errors.startDate = 'Valid start date is required';
    }
    if (!editForm.endDate || !isValidDate(editForm.endDate)) {
      errors.endDate = 'Valid end date is required';
    }
    if (isValidDate(editForm.startDate) && isValidDate(editForm.endDate)) {
      const start = new Date(editForm.startDate);
      const end = new Date(editForm.endDate);
      if (start > end) {
        errors.startDate = 'Start date cannot be after end date';
      }
    }

    setEditErrors(errors);

    // Check if any errors
    if (Object.values(errors).some(error => error)) {
      setSnackbar({
        open: true,
        message: 'Please fix the errors before saving',
        severity: 'error'
      });
      return;
    }

    try {
      let updatedCompetition = { ...editingCompetition };

      // Check if start date changed to trigger recursive shift
      const oldStartDate = editingCompetition.competition.startDate 
        ? String(editingCompetition.competition.startDate).split('T')[0] 
        : '';
      
      let customMessage = null;
      if (oldStartDate && editForm.startDate && oldStartDate !== editForm.startDate) {
        // Shift the entire competition (sessions, timeBlocks, endDate) based on new start date
        updatedCompetition = shiftCompetitionDates(updatedCompetition, editForm.startDate);
        customMessage = 'Competition dates and session schedule adjusted automatically.';
      }

      // Apply specific form updates (overwriting shifted values if user explicitly changed them)
      updatedCompetition = {
        ...updatedCompetition,
        competition: {
          ...updatedCompetition.competition,
          name: editForm.name,
          startDate: editForm.startDate,
          endDate: editForm.endDate
        }
      };

      // saveCompetitionVersion is a thunk function, not createAsyncThunk
      // It will trigger saveVersionSuccess which shows the toast
      await dispatch(saveCompetitionVersion(updatedCompetition, customMessage));

      // Small delay to ensure save completes
      await new Promise(resolve => setTimeout(resolve, 500));

      // Reload competitions to get updated data
      const competitions = await competitionService.getAllCompetitions();
      dispatch(setCompetitions(competitions));

      setEditModalOpen(false);
      // Don't show duplicate toast - Redux saveVersionSuccess already shows one
    } catch (error) {
      console.error('Error updating competition:', error);
      setSnackbar({
        open: true,
        message: `Error updating competition: ${error.message}`,
        severity: 'error'
      });
    }
  };

  const handleDuplicate = async (event, competition) => {
    event.stopPropagation(); // Prevent triggering the competition click
    try {
      await dispatch(duplicateCompetition(competition)).unwrap();
      setSnackbar({
        open: true,
        message: 'Competition duplicated successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error duplicating competition:', error);
      setSnackbar({
        open: true,
        message: `Error duplicating competition: ${error.message}`,
        severity: 'error'
      });
    }
  };

  const handleCompetitionClick = (competition) => {
    // Set current competition in Redux store before navigating
    const competitionWithAccess = {
      ...competition,
      competition: {
        ...competition.competition,
        user_id: user?.id // Ensure user_id is set
      }
    };
    
    dispatch(setCurrentCompetition(competitionWithAccess));
    
    // Then navigate
    const { id } = competitionWithAccess?.competition;
    if (!id) {
      console.warn('No valid identifier found for competition:', competitionWithAccess);
      return;
    }
    navigate(`/competition/${id}`);
  };

  const defaultColors = {
    primary: theme.palette.primary.main,
    secondary: theme.palette.secondary.main,
  };

  return (
    <Box
      sx={{
        height: '100vh',
        // background: `linear-gradient(135deg, ${theme.palette.custom.gradientStart}, ${theme.palette.custom.gradientEnd})`,
        pt: 3,
        pb: 6,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Container maxWidth="lg" sx={{ flex: 1, overflow: 'auto' }}>
        {/* Search Section */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            mb: 4,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search for Sanction or Competition Name"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              sx: theme => ({
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.05)'
                  : 'rgba(0, 0, 0, 0.1)',
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(0, 0, 0, 0.15)',
                },
              }),
            }}
            sx={{ maxWidth: 600 }}
          />
          <Button
            variant="contained"
            onClick={() => navigate('/competition/create')}
            startIcon={<AddIcon />}
            sx={{
              bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'grey.100',
              color: theme => theme.palette.text.primary,
              '&:hover': {
                bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'grey.200',
              },
              textTransform: 'none',
              boxShadow: 1
            }}
          >
            Create
          </Button>
          <Button
            variant="contained"
            onClick={() => setImportModalOpen(true)}
            startIcon={<UploadFileIcon />}
            sx={{
              bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'grey.100',
              color: theme => theme.palette.text.primary,
              '&:hover': {
                bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'grey.200',
              },
              textTransform: 'none',
              boxShadow: 1
            }}
          >
            Import
          </Button>
          <ImportModal
            open={importModalOpen}
            onClose={() => setImportModalOpen(false)}
            onImport={useCompetitionImport(() => {
              setImportModalOpen(false);
              setSnackbar({
                open: true,
                message: 'Competition imported successfully',
                severity: 'success'
              });
            }, setSnackbar)}
            onFileSelect={() => {}}
            existingCompetitions={competitions}
          />
        </Box>

        {backendError && (
          <Alert 
            severity="warning" 
            sx={{ mb: 2 }}
          >
            Unable to connect to server. Please check your internet connection or try again later.
          </Alert>
        )}
        
        {/* Competitions List */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Button
                size="small"
                onClick={() => handleSort('date')}
                variant={sortConfig.field === 'date' ? 'contained' : 'outlined'}
                sx={{
                  bgcolor: sortConfig.field === 'date'
                    ? (theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'grey.100')
                    : 'transparent',
                  color: theme => theme.palette.text.primary,
                  '&:hover': {
                    bgcolor: sortConfig.field === 'date'
                      ? (theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'grey.200')
                      : (theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'grey.100')
                  },
                  borderColor: theme => theme.palette.divider,
                  textTransform: 'none',
                  boxShadow: sortConfig.field === 'date' ? 1 : 0
                }}
                endIcon={sortConfig.field === 'date' && (
                  sortConfig.direction === 'asc' ? '↑' : '↓'
                )}
              >
                Sort by Date
              </Button>
              <Button
                size="small"
                onClick={() => handleSort('name')}
                variant={sortConfig.field === 'name' ? 'contained' : 'outlined'}
                sx={{
                  bgcolor: sortConfig.field === 'name'
                    ? (theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'grey.100')
                    : 'transparent',
                  color: theme => theme.palette.text.primary,
                  '&:hover': {
                    bgcolor: sortConfig.field === 'name'
                      ? (theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'grey.200')
                      : (theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'grey.100')
                  },
                  borderColor: theme => theme.palette.divider,
                  textTransform: 'none',
                  boxShadow: sortConfig.field === 'name' ? 1 : 0
                }}
                endIcon={sortConfig.field === 'name' && (
                  sortConfig.direction === 'asc' ? '↑' : '↓'
                )}
              >
                Sort by Name
              </Button>
            </Box>
            {isLoading ? (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 6,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <CircularProgress />
                <Typography variant="h6" gutterBottom>
                  Loading competitions...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Please wait while we fetch your competitions
                </Typography>
              </Box>
            ) : sortedCompetitions.length > 0 ? (
              <List>
                {sortedCompetitions.map((competition) => {
                  const competitionColors = competition?.competition?.colors || defaultColors;
                  return (
                    <Box 
                      key={`competition-${competition.competition?.id || competition.competition?.sanctionId}`}
                    >
                      <ListItem
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        transition: 'all 0.2s',
                        borderLeft: `4px solid ${competitionColors.primary}`,
                        background: `linear-gradient(90deg, ${competitionColors.secondary}15 0%, transparent 100%)`,
                        '&:hover': {
                          backgroundColor: `${competitionColors.secondary}30`,
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {highlightText(competition.competition.name, searchQuery)}
                            {isNewCompetition(competition) && (
                              <Chip 
                                label="NEW" 
                                size="small" 
                                color="primary"
                                sx={{ 
                                  height: '20px',
                                  backgroundColor: theme.palette.success.main,
                                  '& .MuiChip-label': {
                                    px: 1,
                                    py: 0,
                                    fontSize: '0.7rem',
                                    fontWeight: 'bold'
                                  }
                                }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          (() => {
                            try {
                              const createSafeLocalDates = (dateStr) => {
                                if (!dateStr) return null;
                                const cleanDateStr = String(dateStr).split('T')[0];
                                if (cleanDateStr.includes('-')) {
                                  const [year, month, day] = cleanDateStr.split('-').map(Number);
                                  return new Date(year, month - 1, day);
                                }
                                return new Date(dateStr);
                              };

                              const startDate = createSafeLocalDates(competition.competition.startDate);
                              const endDate = createSafeLocalDates(competition.competition.endDate);
                                
                              const formatDate = (date) => {
                                if (!date) return 'TBD';
                                try {
                                  return format(date, 'MM/dd/yyyy');
                                } catch {
                                  return 'Invalid Date';
                                }
                              };

                              const dateText = `${formatDate(startDate)} - ${formatDate(endDate)}`;
                              const sessionsText = `${competition.sessions?.length || 0} ${(competition.sessions?.length || 0) === 1 ? 'session' : 'sessions'}`;

                              return (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                                  <Typography variant="body2" component="span" sx={{ whiteSpace: 'nowrap' }}>
                                    {dateText}
                                  </Typography>
                                  <Chip 
                                    label={getStatusLabel(competition.competition.status)}
                                    color={getStatusColor(competition.competition.status)}
                                    size="small"
                                    sx={{ height: '20px' }}
                                  />
                                  <Typography variant="body2" component="span" sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                                    • {sessionsText}
                                  </Typography>
                                </Box>
                              );
                            } catch (error) {
                              console.error('Date formatting error:', error, competition);
                              return 'Invalid dates';
                            }
                          })()
                        }
                        onClick={() => handleCompetitionClick(competition)}
                        sx={{ cursor: 'pointer' }}
                      />
                      <Box>
                        <IconButton 
                          size="small"
                          onClick={(e) => handleEdit(e, competition)}
                          title="Edit competition"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          size="small"
                          onClick={(e) => handleDuplicate(e, competition)}
                          title="Duplicate competition"
                        >
                          <ContentCopyIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => handleDelete(e, competition)}
                        >
                          <DeleteIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleExpandClick(competition.competition.id)}
                        >
                          {expandedId === competition.competition.id ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </IconButton>
                      </Box>
                    </ListItem>
                    <Collapse
                      in={expandedId === competition.competition.id}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box 
                        sx={{ 
                          p: 2, 
                          pl: 4, 
                          backgroundColor: `${competitionColors.secondary}15`,
                          borderLeft: `4px solid ${competitionColors.primary}`,
                        }}
                      >
                        <Typography variant="body2" sx={{ mb: 3 }}>
                          {highlightText(competition.competition.details, searchQuery)}
                        </Typography>
                        <SessionList 
                          sessions={competition.sessions || []}
                          competitionColors={competition.colors || competitionColors}
                        />
                      </Box>
                    </Collapse>
                  </Box>
                ); // Add semicolon here
              })}
              </List>
            ) : (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 6,
                }}
              >
                <Typography variant="h6" gutterBottom>
                  No competitions found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {searchQuery 
                    ? `No competitions match "${searchQuery}". Try a different search term.`
                    : 'Create a new competition or import existing ones to get started'
                  }
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>

      {/* Quick Edit Modal */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Edit Competition
          {editForm.startDate && editForm.endDate && (
            <Chip 
              label={`${differenceInDays(new Date(editForm.endDate), new Date(editForm.startDate)) + 1} Day${(differenceInDays(new Date(editForm.endDate), new Date(editForm.startDate)) + 1) !== 1 ? 's' : ''}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Competition Name"
              value={editForm.name}
              onChange={(e) => handleNameChange(e.target.value)}
              fullWidth
              required
              error={!!editErrors.name}
              helperText={editErrors.name}
            />
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <TextField
                label="Start Date"
                type="date"
                value={editForm.startDate || ''}
                onChange={(e) => {
                  const newDate = e.target.value;
                  const updates = { startDate: newDate };
                  
                  // Auto-adjust end date if start date is after it
                  if (newDate && editForm.endDate && newDate > editForm.endDate) {
                    updates.endDate = newDate;
                  }
                  
                  setEditForm({ ...editForm, ...updates });
                  setEditErrors({ ...editErrors, startDate: '', endDate: '' });
                }}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                error={!!editErrors.startDate}
                helperText={editErrors.startDate}
              />
              <TextField
                label="End Date"
                type="date"
                value={editForm.endDate || ''}
                onChange={(e) => {
                  const newDate = e.target.value;
                  const updates = { endDate: newDate };
                  
                  // Auto-adjust start date if end date is before it
                  if (newDate && editForm.startDate && newDate < editForm.startDate) {
                    updates.startDate = newDate;
                  }
                  
                  setEditForm({ ...editForm, ...updates });
                  setEditErrors({ ...editErrors, startDate: '', endDate: '' });
                }}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                error={!!editErrors.endDate}
                helperText={editErrors.endDate}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditModalOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          Delete Competition
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to permanently delete this competition?
          </Typography>
          {competitionToDelete && (
            <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 1, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'error.contrastText' }}>
                {competitionToDelete.competition.name}
              </Typography>
              <Typography variant="body2" sx={{ color: 'error.contrastText', opacity: 0.9 }}>
                {competitionToDelete.competition.details || 'No description available'}
              </Typography>
            </Box>
          )}
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Warning:</strong> This action cannot be undone. All competition data, including sessions, teams, gymnasts, and scoring information will be permanently removed.
            </Typography>
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Please type <strong>"DELETE"</strong> to confirm:
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Type DELETE to confirm"
            sx={{ mt: 1 }}
            value={deleteConfirmationText}
            onChange={(e) => setDeleteConfirmationText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteDialogOpen(false);
            setDeleteConfirmationText('');
            setCompetitionToDelete(null);
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={deleteConfirmationText !== 'DELETE'}
          >
            Delete Competition
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Dashboard;
